// ============================================================
// SecaoAvaliacaoCiclo.js — a avaliação de fim de ciclo do atleta
//
// Duas coisas moram aqui:
//
//  PortaDaAvaliacao — o portão. Antes de abrir a Área do Atleta, pergunta
//  se aquele uid já respondeu o ciclo. Já respondeu, entra. Não respondeu,
//  vê o questionário e mais nada.
//
//  SecaoAvaliacaoCiclo — o questionário em si, as mesmas perguntas do
//  papel de 2024/2025 (ver avaliacaoCiclo.js), sem o bloco de
//  identificação: nome, modalidade, núcleo e idade vêm do login e
//  aparecem só para o atleta confirmar que é ele.
//
// O portão é de tela, não de dado. Ele decide o que o painel MOSTRA; não
// é, e não pretende ser, um cadeado sobre a frequência, que continua
// protegida pelas regras do Firestore como sempre esteve. O objetivo aqui
// é colher a resposta de todo mundo, não esconder dado de ninguém.
// ============================================================

import { useState, useEffect, useRef, useMemo } from "react";
import { carregarMinhaAvaliacaoDoCiclo, enviarAvaliacaoDoCiclo } from "../firebase";
import { normalizarModalidade } from "../calculo";
import { hojeLocalISO } from "../dadosDoNucleo";
import { idadeEm } from "../idade";
import { useNomeNucleo } from "../NucleosContexto";
import useIsMobile from "../useIsMobile";
import {
  CICLO, CICLO_ROTULO, ORIENTACAO, PRAZO, SECOES, PERGUNTAS, OBRIGATORIAS, ABERTAS,
  faltamResponder, perguntasDaSecao, dentroDoPrazo, diasAteOPrazo, recadoDoPrazo,
} from "../avaliacaoCiclo";
import {
  AZUL, AZUL_CLARO, ACAO, TEXTO, CINZA_ESC, BRANCO, BORDA, BORDA_FORTE,
  FUNDO_SUAVE, RAIO, ESP, TIPO, PERIGO, PERIGO_CL, OK, SOMBRA,
} from "../tema";
import { Card, Btn, StatusBox } from "../componentes";

// ── O portão ──────────────────────────────────────────────────
export function PortaDaAvaliacao({
  uid, nome, email, nucleo, modalidade, dataNascimento, children,
}) {
  // null = ainda perguntando; false = não respondeu; objeto = respondeu.
  const [resposta, setResposta] = useState(null);
  const [erro, setErro] = useState(null);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    if (!uid) return;
    let ativo = true;
    setErro(null);
    (async () => {
      try {
        const r = await carregarMinhaAvaliacaoDoCiclo(uid, CICLO);
        if (ativo) setResposta(r || false);
      } catch (e) {
        // Sem saber se respondeu, o painel não abre. A leitura é de um
        // documento do próprio atleta: quando ela falha, o painel também
        // não leria a frequência dele, então liberar aqui mostraria uma
        // Área do Atleta vazia e deixaria a resposta sem colher.
        if (ativo) setErro(e && e.message ? e.message : "falha na leitura");
      }
    })();
    return () => { ativo = false; };
  }, [uid, tentativa]);

  if (erro) {
    return (
      <div style={{ maxWidth: 620 }}>
        <Card titulo="Não deu para verificar a sua avaliação">
          <div style={{ ...TIPO.corpo, color: CINZA_ESC, marginBottom: ESP.lg }}>
            A conexão falhou antes de o painel saber se você já respondeu a avaliação
            do ciclo. Tente de novo em alguns segundos.
          </div>
          <Btn onClick={() => { setErro(null); setTentativa(t => t + 1); }}>Tentar de novo</Btn>
        </Card>
      </div>
    );
  }

  if (resposta === null) {
    return (
      <div style={{ maxWidth: 620 }}>
        <Card>
          <div style={{ ...TIPO.corpo, color: CINZA_ESC }}>Carregando...</div>
        </Card>
      </div>
    );
  }

  if (resposta === false) {
    return (
      <SecaoAvaliacaoCiclo
        uid={uid} nome={nome} email={email} nucleo={nucleo}
        modalidade={modalidade} dataNascimento={dataNascimento}
        onEnviada={(r) => setResposta(r)}
      />
    );
  }

  return children;
}

// ── O questionário ────────────────────────────────────────────
export default function SecaoAvaliacaoCiclo({
  uid, nome, email, nucleo, modalidade, dataNascimento, onEnviada,
}) {
  const isMobile = useIsMobile();
  const nomeNucleo = useNomeNucleo();
  const hojeISO = hojeLocalISO();

  const [respostas, setRespostas] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const [cobrando, setCobrando] = useState(false); // já tentou enviar incompleto
  const [enviada, setEnviada] = useState(null);
  const blocos = useRef({});

  const faltam = useMemo(() => faltamResponder(respostas), [respostas]);
  const respondidas = OBRIGATORIAS.length - faltam.length;
  const idade = idadeEm(dataNascimento, hojeISO);
  const diasRestantes = diasAteOPrazo(hojeISO, PRAZO);
  const prazoApertado = diasRestantes !== null && diasRestantes <= 2;

  function responder(id, valor) {
    setRespostas(r => ({ ...r, [id]: valor }));
  }

  async function enviar() {
    const pendentes = faltamResponder(respostas);
    if (pendentes.length > 0) {
      setCobrando(true);
      setErro(
        pendentes.length === 1
          ? "Falta 1 pergunta obrigatória. Ela está marcada em vermelho."
          : `Faltam ${pendentes.length} perguntas obrigatórias. Elas estão marcadas em vermelho.`
      );
      const alvo = blocos.current[pendentes[0]];
      if (alvo && alvo.scrollIntoView) alvo.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setEnviando(true); setErro(null);
    try {
      // As abertas vão sem espaço sobrando nas pontas, e as vazias não vão:
      // campo gravado como "" ocupa coluna na planilha e some na contagem
      // de quem respondeu aquela pergunta.
      const limpas = {};
      PERGUNTAS.forEach(p => {
        const v = String(respostas[p.id] || "").trim();
        if (v) limpas[p.id] = v;
      });
      await enviarAvaliacaoDoCiclo({
        uid, ciclo: CICLO, nome, email, nucleo,
        // Gravada já normalizada. A modalidade vem do cadastro, onde a mesma
        // existe em duas grafias — "Tenis de Mesa" e "Tênis de Mesa" —, e
        // cada resposta nova carregava a grafia do cadastro de quem respondeu.
        // O relatório agrupa pelo texto, então gravar cru era semear a
        // duplicata de novo a cada ciclo.
        modalidade: normalizarModalidade(modalidade),
        idadeNaResposta: idade,
        respostas: limpas,
        noPrazo: dentroDoPrazo(hojeISO, PRAZO),
      });
      setEnviada({ ciclo: CICLO, respostas: limpas });
    } catch (e) {
      setErro(
        "Não deu para enviar agora: " + (e && e.message ? e.message : "falha na gravação") +
        ". As suas respostas continuam preenchidas nesta tela, tente enviar de novo."
      );
    } finally {
      setEnviando(false);
    }
  }

  // Enviada. O painel só abre quando o atleta clica, para a mensagem não
  // passar em branco: quem responde vinte perguntas merece saber que
  // chegou, e sumir a tela na hora parece erro.
  if (enviada) {
    return (
      <div style={{ maxWidth: 620 }}>
        <Card>
          <div style={{ display: "flex", alignItems: "flex-start", gap: ESP.md, marginBottom: ESP.lg }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%", background: "#DCFCE7", color: OK,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 24, fontWeight: 800, flexShrink: 0,
            }}>✓</div>
            <div>
              <div style={{ ...TIPO.titulo, color: TEXTO }}>Respostas enviadas</div>
              <div style={{ ...TIPO.corpo, color: CINZA_ESC, marginTop: ESP.xs }}>
                Obrigado, {primeiroNome(nome)}. A avaliação do ciclo {CICLO_ROTULO} foi registrada
                e vai ser lida pela coordenação do programa.
              </div>
            </div>
          </div>
          <Btn onClick={() => onEnviada && onEnviada(enviada)}>Ir para o meu painel</Btn>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760 }}>
      {/* Abertura: o que é, por que está bloqueando e quem vai ler */}
      <div style={{
        background: BRANCO, border: `1px solid ${BORDA}`, borderRadius: RAIO.lg,
        boxShadow: SOMBRA.cartao, padding: isMobile ? "20px 18px" : "26px 26px",
        marginBottom: ESP.lg,
      }}>
        <div style={{
          display: "inline-block", background: AZUL_CLARO, color: AZUL,
          ...TIPO.rotulo, padding: "5px 11px", borderRadius: RAIO.pilula, marginBottom: ESP.md,
        }}>
          Ciclo {CICLO_ROTULO}
        </div>
        <h1 style={{
          fontSize: isMobile ? 23 : 28, fontWeight: 800, letterSpacing: -0.6,
          lineHeight: 1.2, color: TEXTO, margin: 0,
        }}>
          Avaliação de encerramento do ciclo
        </h1>
        <div style={{ ...TIPO.corpo, color: CINZA_ESC, marginTop: ESP.md }}>
          Antes de entrar no seu painel, conte como foi o seu ciclo no Projeto Revelar
          Talentos. {ORIENTACAO}
        </div>
        <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: ESP.sm }}>
          São {OBRIGATORIAS.length} perguntas de marcar, que levam poucos minutos, e
          {" "}{ABERTAS.length} de escrever, que são opcionais. Quem lê as respostas é a
          coordenação do programa.
        </div>

        {/* Identificação: o que o sistema já sabe, só para conferir */}
        <div style={{
          display: "flex", flexWrap: "wrap", gap: ESP.sm,
          marginTop: ESP.lg, paddingTop: ESP.lg, borderTop: `1px solid ${BORDA}`,
        }}>
          <Etiqueta rotulo="Atleta" valor={nome || "—"} />
          <Etiqueta rotulo="Modalidade" valor={modalidade || "—"} />
          <Etiqueta rotulo="Núcleo" valor={nomeNucleo(nucleo) || nucleo || "—"} />
          <Etiqueta rotulo="Idade" valor={idade !== null ? `${idade} anos` : "—"} />
        </div>
        <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: ESP.sm, lineHeight: 1.5 }}>
          Esses dados vêm do seu cadastro, não precisa escrever o nome. Se algum estiver
          errado, avise a coordenação.
        </div>
      </div>

      <div style={{ marginBottom: ESP.lg }}>
        <StatusBox tipo={prazoApertado ? "aviso" : "info"} msg={recadoDoPrazo(hojeISO, PRAZO)} />
      </div>

      {/* As seções, na ordem do questionário */}
      {SECOES.map((secao, i) => (
        <Card
          key={secao.id}
          titulo={`${i + 1}. ${secao.titulo}`}
          style={{ marginBottom: ESP.lg }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: ESP.xl }}>
            {perguntasDaSecao(secao.id).map(p => (
              <Pergunta
                key={p.id}
                pergunta={p}
                valor={respostas[p.id] || ""}
                onResponder={(v) => responder(p.id, v)}
                faltando={cobrando && faltam.includes(p.id)}
                isMobile={isMobile}
                refBloco={(el) => { blocos.current[p.id] = el; }}
              />
            ))}
          </div>
        </Card>
      ))}

      {/* Envio */}
      <div style={{
        background: BRANCO, border: `1px solid ${BORDA}`, borderRadius: RAIO.lg,
        boxShadow: SOMBRA.cartao, padding: isMobile ? "18px" : "22px 24px",
      }}>
        {erro && <div style={{ marginBottom: ESP.lg }}><StatusBox tipo="erro" msg={erro} /></div>}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: ESP.md, flexWrap: "wrap",
        }}>
          <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
            {faltam.length === 0
              ? `As ${OBRIGATORIAS.length} perguntas obrigatórias estão respondidas.`
              : `${respondidas} de ${OBRIGATORIAS.length} perguntas obrigatórias respondidas.`}
          </div>
          <Btn onClick={enviar} disabled={enviando}>
            {enviando ? "Enviando..." : "Enviar e abrir o meu painel"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── Peças da tela ─────────────────────────────────────────────

function primeiroNome(nome) {
  return String(nome || "").trim().split(/\s+/)[0] || "atleta";
}

function Etiqueta({ rotulo, valor }) {
  return (
    <div style={{
      background: FUNDO_SUAVE, border: `1px solid ${BORDA}`, borderRadius: RAIO.md,
      padding: "8px 12px", minWidth: 0,
    }}>
      <div style={{ ...TIPO.rotulo, color: CINZA_ESC, fontSize: 10 }}>{rotulo}</div>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: TEXTO, marginTop: 2 }}>{valor}</div>
    </div>
  );
}

// Uma pergunta: o enunciado e as opções, ou o enunciado e a caixa de texto.
//
// A opção é um <label> com um <input type="radio"> de verdade dentro.
// Caixinha desenhada com <div> fica bonita e não responde a teclado, e
// este formulário vai ser respondido por adolescente no celular e por
// quem usa leitor de tela no laboratório do IFCE.
function Pergunta({ pergunta, valor, onResponder, faltando, isMobile, refBloco }) {
  const { id, texto, opcoes, aberta } = pergunta;
  return (
    <div
      ref={refBloco}
      style={{
        // A faixa vermelha à esquerda é o que leva o olho até a pendência
        // depois de uma tentativa de envio. Em repouso ela existe
        // transparente, para a pergunta não dar um salto lateral quando
        // aparece.
        borderLeft: `3px solid ${faltando ? PERIGO : "transparent"}`,
        background: faltando ? PERIGO_CL + "55" : "transparent",
        borderRadius: faltando ? RAIO.sm : 0,
        padding: `${faltando ? ESP.sm : 0}px 0 ${faltando ? ESP.sm : 0}px ${ESP.md}px`,
        marginLeft: -ESP.md,
      }}
    >
      <div style={{
        fontSize: 14.5, fontWeight: 600, color: TEXTO, lineHeight: 1.5, marginBottom: ESP.md,
      }}>
        {texto}
        {!aberta && <span style={{ color: PERIGO, marginLeft: 4 }}>*</span>}
        {aberta && (
          <span style={{ ...TIPO.apoio, color: CINZA_ESC, fontWeight: 500, marginLeft: 6 }}>
            (opcional)
          </span>
        )}
      </div>

      {aberta ? (
        <textarea
          value={valor}
          onChange={(e) => onResponder(e.target.value)}
          rows={3}
          maxLength={800}
          placeholder="Escreva aqui, se quiser"
          aria-label={texto}
          style={{
            width: "100%", border: `1px solid ${BORDA_FORTE}`, borderRadius: RAIO.md,
            padding: "11px 13px", fontSize: 13.5, fontFamily: "inherit", color: TEXTO,
            background: BRANCO, outline: "none", boxSizing: "border-box", resize: "vertical",
            lineHeight: 1.6,
          }}
        />
      ) : (
        <div
          role="radiogroup"
          aria-label={texto}
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(170px, 1fr))",
            gap: ESP.sm,
          }}
        >
          {opcoes.map(op => {
            const marcada = valor === op;
            return (
              <label
                key={op}
                style={{
                  display: "flex", alignItems: "center", gap: ESP.sm,
                  border: `1.5px solid ${marcada ? ACAO : BORDA_FORTE}`,
                  background: marcada ? AZUL_CLARO : BRANCO,
                  color: marcada ? AZUL : TEXTO,
                  borderRadius: RAIO.md, padding: "12px 14px",
                  fontSize: 13.5, fontWeight: marcada ? 700 : 500,
                  cursor: "pointer", lineHeight: 1.35,
                }}
              >
                <input
                  type="radio"
                  name={id}
                  value={op}
                  checked={marcada}
                  onChange={() => onResponder(op)}
                  style={{ accentColor: ACAO, width: 17, height: 17, flexShrink: 0, margin: 0 }}
                />
                <span>{op}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
